import { useVersionCheck } from '@worklogix/web-common';
import { useEffect, useRef, useState } from 'react';

/** No tap for this long (and no punch on screen) means nobody is using the kiosk. */
const IDLE_BEFORE_RELOAD_MS = 30_000;
/** How often to re-check idleness once an update is waiting. */
const IDLE_CHECK_MS = 5_000;
/** Long enough for the yellow overlay to register before the page reloads. */
const RELOAD_DELAY_MS = 2_000;
/**
 * If a reload for a build did not land us on it (e.g. a CDN still serving the old
 * index), wait this long before trying that same build again — never a reload loop.
 */
const RETRY_SAME_BUILD_MS = 5 * 60_000;
const ATTEMPT_KEY = 'worklogix.kiosk.reloadAttempt';

interface ReloadAttempt {
  v: string;
  at: number;
}

function readAttempt(): ReloadAttempt | null {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    return raw === null ? null : (JSON.parse(raw) as ReloadAttempt);
  } catch {
    return null;
  }
}

function recordAttempt(v: string): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ v, at: Date.now() }));
  } catch {
    // Storage unavailable — the reload still happens, only the loop guard is lost.
  }
}

interface UpdateOverlayProps {
  /** A punch is on screen (confirming, or its green/red overlay) — never reload over it. */
  busy: boolean;
}

/**
 * Applies a newer deployed build WITHOUT anyone having to press Refresh. Once an
 * update is detected the kiosk waits until it is idle — no tap for 30s and no
 * punch on screen — so it never reloads under someone typing a PIN, then shows a
 * full-screen YELLOW overlay and reloads itself. Queued punches live in IndexedDB
 * and survive the reload. The Refresh button stays as a manual fallback.
 */
export function UpdateOverlay({ busy }: UpdateOverlayProps): JSX.Element | null {
  const { updateAvailable, latestVersion } = useVersionCheck();
  const lastActivity = useRef(Date.now());
  const busyRef = useRef(busy);
  const [reloading, setReloading] = useState(false);

  busyRef.current = busy;

  useEffect(() => {
    const touch = (): void => {
      lastActivity.current = Date.now();
    };
    window.addEventListener('pointerdown', touch, { capture: true });
    window.addEventListener('keydown', touch, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', touch, { capture: true });
      window.removeEventListener('keydown', touch, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!updateAvailable || latestVersion === null || reloading) {
      return;
    }
    const timer = window.setInterval(() => {
      const idle = Date.now() - lastActivity.current >= IDLE_BEFORE_RELOAD_MS;
      const attempt = readAttempt();
      const recentlyTried =
        attempt !== null &&
        attempt.v === latestVersion &&
        Date.now() - attempt.at < RETRY_SAME_BUILD_MS;
      if (idle && !busyRef.current && !recentlyTried) {
        recordAttempt(latestVersion);
        setReloading(true);
      }
    }, IDLE_CHECK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [updateAvailable, latestVersion, reloading]);

  useEffect(() => {
    if (!reloading) {
      return;
    }
    const timer = window.setTimeout(() => {
      window.location.reload();
    }, RELOAD_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [reloading]);

  if (!reloading) {
    return null;
  }

  return (
    <div className="overlay overlay--info" role="alertdialog" aria-labelledby="update-title">
      <div className="overlay__icon" aria-hidden="true">
        ↻
      </div>
      <div id="update-title" className="overlay__title">
        Updating — one moment
      </div>
      <div className="overlay__detail">WorkLogix is loading a new version.</div>
      <button
        type="button"
        className="overlay__button"
        onClick={() => {
          window.location.reload();
        }}
      >
        Refresh
      </button>
    </div>
  );
}
